/**
 * Founders Beta Program
 * /community/founders-beta-program
 *
 * The definitive "join the beta" page. Communicates the free-for-life promise,
 * Founding Member pricing, what gets built, and why roofers should care.
 */

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { SectionBadge } from '@/components/marketing';
import { getAuthLink } from '@/lib/links';
import { webPageSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Crown,
  Lock,
  Map,
  Handshake,
  Users,
  ArrowRight,
  Check,
  CheckCircle2,
  DollarSign,
  MessageSquare,
  Sparkles,
  Phone,
  BarChart3,
  Rocket,
  ChevronDown,
  Bell,
  X,
  Activity,
} from 'lucide-react';

// ─────────────────────────────────────────
// DATA
// ─────────────────────────────────────────

const stats = [
  { value: '500', label: 'Leads/month — free during beta', icon: Activity },
  { value: '30%', label: 'Founding Member rate — off forever', icon: Lock },
  { value: '30 days', label: 'Notice before billing ever starts', icon: Bell },
  { value: '$0', label: 'Cost to join right now', icon: DollarSign },
];

const benefits = [
  {
    icon: Lock,
    title: 'Founding Member Pricing — Locked Forever',
    description:
      "Pro drops from $79 to $55/user/mo. Scale drops from $99 to $69/user/mo. This is not a promo — it's your permanent rate. Competitors raise prices every year. We freeze yours.",
    highlight: '30% off · forever',
    color: '#10b981',
  },
  {
    icon: Bell,
    title: '30-Day Notice Before Billing Starts',
    description:
      "No mystery end date. When we transition out of beta, you'll receive 30 days notice before any charge is attempted. Time to choose your plan, add a payment method, or cancel — on your terms.",
    highlight: 'Zero surprises',
    color: '#4ade80',
  },
  {
    icon: Map,
    title: 'Direct Roadmap Influence',
    description:
      "You're not a user — you're a co-builder. Monthly feedback calls (optional), feature request voting, and direct Slack access to the founding team. What you ask for gets built.",
    highlight: 'Shape the product',
    color: '#60a5fa',
  },
  {
    icon: Handshake,
    title: 'Free Migration Concierge',
    description:
      'Every beta user gets help migrating from their current CRM (JobNimbus, AccuLynx, Buildertrend, spreadsheets). We do the technical work — you focus on your business.',
    highlight: 'White-glove migration',
    color: '#c084fc',
  },
  {
    icon: Sparkles,
    title: 'All Features — Fully Unlocked',
    description:
      'During beta, you get full Pro/Scale access to every feature: proposals, e-sign, territory tracking, canvassing, marketing suite, call tracking, satellite estimator. Everything.',
    highlight: 'Full platform access',
    color: '#06b6d4',
  },
  {
    icon: Users,
    title: 'Private Founders Community',
    description:
      'Connect with other growth-minded roofing contractors. Share strategies, close rates, canvassing scripts, and marketing tactics in a private community built exclusively for Rooflyzer users.',
    highlight: 'Peer network',
    color: '#4ade80',
  },
];

const whatsIncluded = [
  {
    category: 'CRM & Sales',
    icon: BarChart3,
    items: [
      'Full lead pipeline with stages and source tracking',
      'Contact & account management',
      'Proposal builder with e-signature',
      'Contract management & digital signing',
      'Job costing & profitability tracking',
      'Commission management',
      'Team management (up to 10 users on Pro)',
    ],
  },
  {
    category: 'Field Tools',
    icon: Map,
    items: [
      'Territory & ZIP code management',
      'Real-time storm & hail tracking',
      'Door-to-door canvassing (GPS-enabled)',
      'Mobile-first — full feature parity on phones',
      'Google Reviews request automation',
      'Customer portal with payment links',
      'Job photo documentation',
    ],
  },
  {
    category: 'Marketing & Comms',
    icon: MessageSquare,
    items: [
      'ConnectIQ Email Marketing (campaigns + journeys)',
      'ConnectIQ SMS Marketing with lead nurture AI',
      'Transactional email & SMS automations',
      'JourneyMaker drag-and-drop automation builder',
      'Custom sending domain — no extra charge',
      'Broadcast campaigns & segment targeting',
      'TCPA-compliant opt-in/opt-out management',
    ],
  },
  {
    category: 'Tracking & Intel',
    icon: Phone,
    items: [
      'PhoneIQ call tracking (attribution per source)',
      'Local + toll-free number provisioning',
      'Call recording & voicemail transcription',
      'IVR routing & business hours rules',
      'SmartRoof Enhanced Satellite Estimator',
      'Instant Roof Calculator embed widget',
      'AI lead scoring & intent analysis',
    ],
  },
];

const roadmap = [
  { quarter: 'Q1 2026', status: 'done', label: 'Core CRM, pipeline, proposals, canvassing' },
  { quarter: 'Q2 2026', status: 'done', label: 'JourneyMaker, email marketing, storm tracking' },
  { quarter: 'Q2 2026', status: 'active', label: 'SMS marketing, call tracking, PhoneIQ launch' },
  {
    quarter: 'Q3 2026',
    status: 'next',
    label: 'AI lead scoring, advanced analytics, SmartRoof Enhanced Satellite',
  },
  { quarter: 'Q3 2026', status: 'next', label: 'QuickBooks & Xero integration, commission portal' },
  {
    quarter: 'Q4 2026',
    status: 'planned',
    label: 'Gamification, voice AI assistant, financing tools',
  },
  {
    quarter: 'Q4 2026',
    status: 'planned',
    label: 'Commercial launch — Founding Member billing begins',
  },
];

const faqs = [
  {
    question: 'How long is the beta free?',
    answer:
      "There is no fixed end date — we will not spring charges on you. When we move to commercial launch, every active user gets at least 30 days notice before any charge is attempted. You will have time to choose your plan or cancel. We're transparent about this because we want you to trust us.",
  },
  {
    question: 'What exactly is a Founding Member?',
    answer:
      'A Founding Member is anyone who signs up during beta and remains an active user when billing starts. You automatically receive a 30% permanent discount on your base plan — forever. Pro goes from $79 to $55/user/mo. Scale goes from $99 to $69/user/mo. This is not a first-year discount. It is your price for the life of your account.',
  },
  {
    question: 'Is there a 500 leads/month limit?',
    answer:
      'Yes — during beta all accounts are capped at 500 new leads per month. This is a stability measure, not a monetization gate. At commercial launch, Free plans cap at 50 active leads, Pro and Scale are unlimited.',
  },
  {
    question: 'Do I need a credit card to sign up?',
    answer:
      "No. The beta is completely free and requires nothing but an email address to get started. A payment method will only be requested when billing starts — and you'll have 30 days notice before that happens.",
  },
  {
    question: 'Can I really migrate from my current CRM?',
    answer:
      'Yes. Our implementation team will help you export your data from JobNimbus, AccuLynx, Buildertrend, or even spreadsheets, and import it into Rooflyzer. We handle the technical work. Expected migration time: 1–3 business days for most accounts.',
  },
  {
    question: 'What happens to my add-ons (marketing, phone numbers) during beta?',
    answer:
      'All add-on features are fully unlocked during beta at no cost. ConnectIQ Marketing Suite, Transactional Comms, PhoneIQ call tracking, and SmartRoof Enhanced Satellite — all available. When billing starts, add-ons will be opt-in at their listed prices. You choose which ones to keep.',
  },
  {
    question: 'How is Rooflyzer different from the apps I already use?',
    answer:
      'Most roofing CRMs are built around project management and charge $200–$500/mo for just that. Rooflyzer OS unifies CRM, marketing, call tracking, estimating, and field ops in one platform — at a lower total cost than any one of those categories on legacy tools. And it was built from the ground up for roofers, not adapted from generic construction software.',
  },
];

interface FaqItemProps {
  question: string;
  answer: string;
}
const FaqItem = ({ question, answer }: FaqItemProps) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="group bg-white/5 rounded-xl border border-white/10 overflow-hidden cursor-pointer hover:border-white/20 transition-colors"
      onClick={() => setOpen(!open)}
    >
      <div className="flex items-center justify-between p-5">
        <span className="text-white font-medium text-sm sm:text-base pr-4">{question}</span>
        <ChevronDown
          className={`size-4 text-slate-400 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </div>
      {open && (
        <div className="px-5 pb-5 text-white/70 text-sm leading-relaxed border-t border-white/5 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────

/**
 * Render the founders beta offer, benefits, roadmap, FAQs, and registration links,
 * with page and FAQ structured data for search engines.
 */
export const FoundersBetaPage = () => {
  const pageSchema = webPageSchema({
    name: 'Founders Beta Program — Rooflyzer OS',
    description:
      'Join Rooflyzer OS free during beta. Founding Members lock in 30% off forever. Full platform access, 500 leads/month, 30-day notice before billing.',
    url: '/community/founders-beta-program',
    datePublished: '2026-02-01',
    dateModified: '2026-03-19',
  });
  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Founders Beta — Free Access + 30% Off Forever | Rooflyzer OS"
        description="Join Rooflyzer OS free during beta. All features unlocked, 500 leads/month, no credit card. Founding Members lock in 30% off their plan forever. 30 days notice before billing starts."
        canonicalUrl="/community/founders-beta-program"
        keywords="rooflyzer beta free, roofing CRM free trial, roofing software founding member, rooflyzer founders program, free roofing CRM"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-24">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Community', url: '/community/founders-beta-program' },
              { name: 'Founders Beta', url: '/community/founders-beta-program' },
            ]}
            className="mb-8"
          />

          {/* ── Hero ────────────────────────────── */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            {/* Beta active pill */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-900 border border-slate-700 mb-6 font-mono text-xs">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-slate-300 font-semibold uppercase tracking-wider">
                Beta Active — Full Platform Unlocked
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight tracking-tight">
              Help Us Build the{' '}
              <span className="text-white underline decoration-emerald-400/40 decoration-2 underline-offset-8">
                Roofing Operating System
              </span>{' '}
              the Industry Actually Deserves
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-6 max-w-3xl mx-auto leading-relaxed">
              The "Big Four" roofing CRMs charge $200–$500/mo, raise prices every year, and still
              deliver bad support. We&apos;re building something different — and we want the best
              roofers in the country to shape it with us.
            </p>

            <p className="text-slate-400 text-base mb-10 max-w-2xl mx-auto leading-relaxed">
              During beta: <strong className="text-white">all features are free</strong>, all plans
              unlocked, up to 500 leads/month. When we launch commercially, Founding Members
              permanently lock in{' '}
              <strong className="text-emerald-400">30% off their plan rate, for life</strong>.
              You&apos;ll get 30 days notice before anything changes. No surprises.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                size="lg"
                className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 text-base shadow-sm"
                onClick={() => window.location.assign(getAuthLink('/register?ref=founders-beta'))}
              >
                Join the Beta — It&apos;s Free
                <Rocket className="ml-2 size-5" />
              </Button>
              <a
                href="/pricing"
                className="text-slate-400 text-sm hover:text-white underline underline-offset-4"
              >
                See full pricing details →
              </a>
            </div>
          </section>

          {/* ── Stats Row ───────────────────────── */}
          <section className="mb-20">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800 text-center"
                >
                  <stat.icon className="size-5 text-slate-400 mx-auto mb-3" />
                  <p className="text-3xl font-bold text-white mb-1 font-mono">{stat.value}</p>
                  <p className="text-slate-400 text-xs leading-tight">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Why The Industry Is Broken ───────── */}
          <section className="mb-20">
            <div className="bg-slate-900/60 rounded-3xl p-8 sm:p-12 border border-slate-800">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-6">
                The Problem With Every Roofing CRM You&apos;ve Tried
              </h2>
              <div className="grid md:grid-cols-2 gap-8 items-start">
                <div className="space-y-4">
                  {[
                    {
                      title: 'JobNimbus',
                      price: '$250+/mo',
                      problem: 'Price raised 3 times in 2 years. Basic features still cost extra.',
                    },
                    {
                      title: 'Buildertrend',
                      price: '$499+/mo',
                      problem:
                        'Jumped 67% from $299 to $499. Designed for construction, not roofers.',
                    },
                    {
                      title: 'AccuLynx',
                      price: '$350+/mo',
                      problem:
                        'Annual price bumps with limited transparency. No marketing tools built in.',
                    },
                    {
                      title: 'Generic CRMs (HubSpot, Salesforce)',
                      price: '$200–$800/mo',
                      problem: 'Not built for roofing. Requires expensive consultants to set up.',
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 bg-slate-950/50 rounded-xl p-4 border border-red-500/10"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <X className="size-4 text-red-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-white font-semibold text-sm">{item.title}</span>
                          <span className="text-red-400 text-xs font-bold font-mono">
                            {item.price}
                          </span>
                        </div>
                        <p className="text-slate-400 text-xs leading-relaxed">{item.problem}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="bg-slate-900/90 rounded-2xl p-6 border border-slate-700/80 mb-4">
                    <Crown className="h-7 w-7 text-emerald-400 mb-4" />
                    <h3 className="text-xl font-bold text-white mb-3">
                      What We&apos;re Building Instead
                    </h3>
                    <ul className="space-y-2.5">
                      {[
                        'Built exclusively for roofing contractors — every flow, every field, every report',
                        'All-in-one: CRM + marketing + call tracking + estimating + field ops',
                        'Dramatically cheaper than assembling separate tools',
                        'Support that answers in hours, not days',
                        "Pricing that doesn't punish your loyalty",
                        'Roadmap driven by the people actually using it',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2.5 text-slate-200 text-sm">
                          <CheckCircle2 className="size-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-between bg-slate-800/80 rounded-xl px-5 py-3 border border-slate-700">
                    <span className="text-white font-semibold text-sm">
                      Rooflyzer (Founding Member)
                    </span>
                    <div className="text-right">
                      <span className="text-emerald-400 font-mono font-bold">$55/user/mo</span>
                      <span className="text-slate-400 text-xs block font-mono">
                        🔒 Locked forever
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Founding Member Benefits ─────────── */}
          <section className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4">
              What Founding Members Get
            </h2>
            <p className="text-white/55 text-center mb-12 max-w-2xl mx-auto">
              These benefits are exclusive to beta users who remain active when billing starts. Not
              a trial. Not a first-year discount. Permanent.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((benefit, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-colors relative overflow-hidden"
                >
                  <div className="absolute top-3 right-3">
                    <span
                      className="text-[10px] font-bold uppercase tracking-wider"
                      style={{ color: benefit.color }}
                    >
                      {benefit.highlight}
                    </span>
                  </div>
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${benefit.color}15` }}
                  >
                    <benefit.icon className="size-5" style={{ color: benefit.color }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ── Everything Included ──────────────── */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <SectionBadge text="Full Platform — All Unlocked During Beta" />
              <h2 className="text-2xl sm:text-3xl font-bold text-white mt-5 mb-3">
                Every Feature. Available Now. Free.
              </h2>
              <p className="text-white/55 max-w-xl mx-auto">
                Here&apos;s what you get access to from day one — no upgrade required during beta.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {whatsIncluded.map((cat) => (
                <div
                  key={cat.category}
                  className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <cat.icon className="size-4 text-slate-400" />
                    <span className="text-slate-200 font-mono font-semibold text-xs uppercase tracking-wider">
                      {cat.category}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-xs text-slate-300">
                        <Check className="size-3 text-emerald-400 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>

          {/* ── Roadmap ──────────────────────────── */}
          <section className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
                Where We&apos;re Headed
              </h2>
              <p className="text-white/55 max-w-xl mx-auto">
                You&apos;re joining at a pivotal moment. Here&apos;s the roadmap — and you can
                influence where we go next.
              </p>
            </div>
            <div className="space-y-3 max-w-3xl mx-auto">
              {roadmap.map((item, i) => (
                <div
                  key={i}
                  className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                    item.status === 'done'
                      ? 'bg-slate-900/80 border-slate-700'
                      : item.status === 'active'
                        ? 'bg-slate-900/90 border-slate-600 shadow-sm'
                        : item.status === 'next'
                          ? 'bg-slate-900/40 border-slate-800'
                          : 'bg-slate-950/40 border-slate-900'
                  }`}
                >
                  <div
                    className={`w-3 h-3 rounded-full flex-shrink-0 ${
                      item.status === 'done'
                        ? 'bg-emerald-400'
                        : item.status === 'active'
                          ? 'bg-emerald-400 animate-pulse'
                          : item.status === 'next'
                            ? 'bg-slate-600'
                            : 'bg-slate-800'
                    }`}
                  />
                  <span className="text-slate-500 font-mono text-xs w-16 flex-shrink-0">
                    {item.quarter}
                  </span>
                  <span
                    className={`text-sm ${
                      item.status === 'done'
                        ? 'text-slate-200'
                        : item.status === 'active'
                          ? 'text-white font-medium'
                          : item.status === 'next'
                            ? 'text-slate-300'
                            : 'text-slate-500'
                    }`}
                  >
                    {item.label}
                  </span>
                  {item.status === 'done' && (
                    <span className="ml-auto font-mono text-xs text-emerald-400 font-medium flex-shrink-0">
                      ✓ Done
                    </span>
                  )}
                  {item.status === 'active' && (
                    <span className="ml-auto font-mono text-xs text-cyan-400 font-medium flex-shrink-0">
                      In progress
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* ── Is This For You ──────────────────── */}
          <section className="mb-20">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Is This Program For You?
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="bg-slate-900/60 rounded-2xl p-6 border border-slate-800">
                <h3 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                  <span className="text-emerald-400 font-mono">✓</span> You&apos;re a Great Fit
                  If...
                </h3>
                <ul className="space-y-3">
                  {[
                    'You run a roofing company — solo to 50+ employees',
                    "You're frustrated with the price or limitations of your current CRM",
                    "You're willing to share honest feedback monthly",
                    'You want to influence the product roadmap',
                    'You value performance and data, not just a contact book',
                    'You want to close more jobs without paying more per tool',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-300 text-sm">
                      <CheckCircle2 className="size-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-950/40 rounded-2xl p-6 border border-slate-900">
                <h3 className="text-base font-semibold text-slate-500 mb-5 flex items-center gap-2">
                  <span className="text-red-400 font-mono">✕</span> Not a Fit If...
                </h3>
                <ul className="space-y-3">
                  {[
                    "You're happy with your current system and don't want to change",
                    'You need 100% feature parity with Buildertrend on day one',
                    "You're not in the roofing industry",
                    "You don't want to provide feedback or engage with the team",
                    "You're looking for a one-time project-management tool, not a full platform",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-500 text-sm">
                      <X className="size-4 text-slate-600 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* ── FAQ ──────────────────────────────── */}
          <section className="mb-20 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <FaqItem key={i} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </section>

          {/* ── Final CTA ────────────────────────── */}
          <section className="text-center">
            <div className="bg-slate-900/80 rounded-3xl p-8 sm:p-14 border border-slate-800 relative overflow-hidden">
              <div className="relative">
                <Crown className="h-12 w-12 text-slate-300 mx-auto mb-6" />
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 tracking-tight">
                  Join the Beta. Lock In Your Rate. Help Build Something Better.
                </h2>
                <p className="text-slate-300 mb-3 max-w-2xl mx-auto leading-relaxed">
                  Thousands of roofers are still paying $500/mo for software their crews hate. You
                  don&apos;t have to. Rooflyzer OS is free right now, it&apos;s built for you, and
                  if you join during beta, your price is locked in forever.
                </p>
                <p className="text-slate-500 font-mono text-xs mb-10">
                  No credit card · No contract · 30 days notice before billing begins
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button
                    size="lg"
                    className="bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 text-base shadow-sm"
                    onClick={() =>
                      window.location.assign(getAuthLink('/register?ref=founders-cta'))
                    }
                  >
                    Start Free — Claim Founding Rate
                    <ArrowRight className="ml-2 size-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-700 text-slate-200 hover:bg-slate-800/60"
                    onClick={() => window.location.assign('/pricing')}
                  >
                    View Full Pricing
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default FoundersBetaPage;
